<template>
  <div class="m-breadcrumb">
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb-list">
        <li v-for="(item, index) in breadcrumbItems" :key="index" class="breadcrumb-item"
          :class="{ 'active': isActiveItem(item) }">
          <button v-if="!isActiveItem(item)" @click="navigateTo(item)" class="breadcrumb-link">
            {{ item.label }}
          </button>
          <span v-else class="breadcrumb-current">
            {{ item.label }}
          </span>
          <span v-if="index < breadcrumbItems.length - 1" class="breadcrumb-separator">
            /
          </span>
        </li>
      </ol>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@/common/composables/useI18n'
import { extractFormFid, useAppStore } from '@/stores/app'
import { getFormTitle } from '@/common/utils/formTitle'
import { resolveStableParentPageSlotId } from '@/common/utils/registryFormIdFromSlotId'
import { resolveAdjacentPageIdForMain, isDirHomePageWorkspace } from '@/common/utils/adjacentSplitView'
import { useDocumentTabsStore } from '@/stores/documentTabs'
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

interface BreadcrumbItem {
  id: string
  label: string
  path: string
}

const props = defineProps<{
  items?: BreadcrumbItem[]
}>()

const emit = defineEmits<{
  'navigate': [item: BreadcrumbItem]
}>()

const router = useRouter()
const route = useRoute()
const appStore = useAppStore()
const documentTabsStore = useDocumentTabsStore()
const { t } = useI18n()

const resolveBreadcrumbParentId = (rawParent: string | undefined): string | undefined => {
  const parent = String(rawParent ?? '').trim()
  if (!parent || parent === 'home') return undefined
  if (appStore.pagesData[parent]) return parent
  const stable = resolveStableParentPageSlotId(
    parent,
    appStore.pagesData as Record<string, unknown>,
    documentTabsStore.tabIdsOrdered,
  )
  if (stable && appStore.pagesData[stable]) return stable
  const root = documentTabsStore.rootTabId
  if (root && appStore.pagesData[root]) return root
  return undefined
}

/**
 * Build breadcrumb chain by traversing parent pages
 */
const buildBreadcrumbChain = (pageId: string, visited: Set<string> = new Set()): BreadcrumbItem[] => {
  // Prevent infinite loops
  if (visited.has(pageId)) {
    return []
  }
  visited.add(pageId)

  const pageData = appStore.pagesData[pageId]
  if (!pageData) {
    return []
  }

  const items: BreadcrumbItem[] = []
  const parentPageId = resolveBreadcrumbParentId(pageData.$parentPageId)

  // Recursively get parent breadcrumbs first
  if (parentPageId && parentPageId !== 'home') {
    const parentItems = buildBreadcrumbChain(parentPageId, visited)
    items.push(...parentItems)
  }

  // Add current page
  const formTitle = getFormTitle(pageData)
  if(!!formTitle){
    items.push({
      id: pageId,
      label: formTitle,
      path: `/platform/${pageId}`
    })
  }

  return items
}

const dropEarlierDuplicateFids = (
  items: BreadcrumbItem[],
  pagesData: Record<string, unknown>,
): BreadcrumbItem[] => {
  const seenFid = new Set<string>()
  const out: BreadcrumbItem[] = []
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i]
    const page = pagesData[item.id] as Record<string, unknown> | undefined
    const fid =
      page?.$fid != null && String(page.$fid) !== ''
        ? String(page.$fid)
        : page
          ? extractFormFid(page) ?? null
          : null
    if (fid) {
      if (seenFid.has(fid)) {
        const isListTab =
          documentTabsStore.tabs.find((t) => t.id === item.id)?.type === 'list'
        if (!isListTab) {
          continue
        }
      }
      seenFid.add(fid)
    }
    out.unshift(item)
  }
  return out
}

const breadcrumbItems = computed(() => {
  if (props.items && props.items.length > 0) {
    return props.items
  }

  // If we're on the home page, only show home
  if (route.name === 'home') {
    return [
      {
        id: 'home',
        label: t('ui.home'),
        path: '/'
      }
    ]
  }

  const currentPageId = appStore.currentPageId
  if (currentPageId && currentPageId !== 'home' && currentPageId !== '') {
    const chain = buildBreadcrumbChain(currentPageId)

    // DirectoryHomePage split: append adjacent preview to breadcrumb chain
    const currentPageData = appStore.pagesData[currentPageId] as Record<string, unknown> | undefined
    if (isDirHomePageWorkspace(currentPageData, currentPageId)) {
      const lastAdjacentId = resolveAdjacentPageIdForMain(
        currentPageId,
        appStore.pagesData as Record<string, unknown>,
        appStore.lastAdjacentPageIdByParent as Record<string, string>,
        documentTabsStore.tabIdsOrdered,
      )
      if (lastAdjacentId && appStore.pagesData[lastAdjacentId]) {
        const adjData = appStore.pagesData[lastAdjacentId]
        const adjTitle = getFormTitle(adjData)
        if (adjTitle) {
          chain.push({
            id: lastAdjacentId,
            label: adjTitle,
            path: `/platform/${lastAdjacentId}`
          })
        }
      }
    }

    const chainDeduped = dropEarlierDuplicateFids(chain, appStore.pagesData)

    // Always start with home
    return [
      {
        id: 'home',
        label: t('ui.home'),
        path: '/'
      },
      ...chainDeduped
    ]
  }

  // Fallback: show home only
  return [
    {
      id: 'home',
      label: t('ui.home'),
      path: '/'
    }
  ]
})

// Only the last item in the chain is "current"
const isActiveItem = (item: BreadcrumbItem): boolean => {
  if (item.id === 'home') {
    return route.name === 'home'
  }
  const items = breadcrumbItems.value
  const lastItem = items.length > 0 ? items[items.length - 1] : null
  return lastItem != null && item.id === lastItem.id
}

const navigateTo = (item: BreadcrumbItem) => {
  if (item.id === 'home') {
    // Clear currentPageId when navigating to home to reset breadcrumb chain
    appStore.updatePageId('')
    router.push('/')
  } else {
    appStore.clearAdjacentForParent(item.id)
    router.push({ name: 'platform', params: { id: item.id } })
  }
  emit('navigate', item)
}

// Watch for route changes to ensure breadcrumb updates
watch(() => route.params.id, (newId, oldId) => {
}, { immediate: true })
</script>

<style scoped lang="scss">
.m-breadcrumb {
  padding-bottom: 0.75rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 1rem;
  min-width: 0;
}

.breadcrumb-list {
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  margin: 0;
  padding: 0;
  align-items: center;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  min-width: 0;

  &.active {
    .breadcrumb-current {
      color: #6c757d;
      font-weight: 500;
    }
  }
}

.breadcrumb-link {
  background: none;
  border: none;
  color: var(--app-text-muted, #6b7280);
  text-decoration: none;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.15s ease-in-out;

  &:hover {
    color: var(--primary-color, #1e3a6e);
    background-color: #e9ecef;
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--primary-color, #1e3a6e) 50%, white);
    outline-offset: 2px;
  }
}

.breadcrumb-current {
  color: #6c757d;
  padding: 0.25rem 0.5rem;
}

.breadcrumb-separator {
  color: #6c757d;
  //margin: 0 0.5rem;
  user-select: none;
}

@media (max-width: 768px) {
  .m-breadcrumb {
    padding-bottom: 0.5rem;
    margin-bottom: 0.625rem;
  }

  .breadcrumb-list {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .breadcrumb-item {
    flex-shrink: 0;
  }

  .breadcrumb-item.active {
    flex: 1 1 auto;
    min-width: 0;
  }

  .breadcrumb-link,
  .breadcrumb-current {
    max-width: 10rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .breadcrumb-item.active .breadcrumb-current {
    max-width: none;
  }
}
</style>

